import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useExpertContext } from '../context/ExpertContext';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiMapPin, FiBook, FiMail, FiUser, FiSend, FiStar, FiMessageSquare, FiCheckCircle } = FiIcons;

const ExpertProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { experts, reviews, addReview, getExpertRating } = useExpertContext();
  
  const [expert, setExpert] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, reviewer_name: '', comment: '' });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const foundExpert = experts.find(e => e.id === id && e.status === 'approved');
    if (foundExpert) {
      setExpert(foundExpert);
    } else if (experts.length > 0) {
      navigate('/');
    }
  }, [id, experts, navigate]);

  // IMPORTANT: Filter for only APPROVED reviews for public display
  const approvedReviews = reviews.filter(r => r.expert_id === id && r.status === 'approved');
  const rating = getExpertRating(id);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingReview(true);
    try {
      await addReview({ ...newReview, expert_id: id });
      setNewReview({ rating: 5, reviewer_name: '', comment: '' });
      setShowReviewForm(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSubmittingReview(false);
    }
  };

  if (!expert) return null;

  return (
    <div className="container mx-auto px-4 py-8">
      <button onClick={() => navigate('/')} className="flex items-center space-x-2 text-slate-500 hover:text-green-600 mb-8 font-bold transition-colors">
        <SafeIcon icon={FiArrowLeft} />
        <span>Back to Grid</span>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <div className="expert-card">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-blue-600 rounded-3xl flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiUser} className="text-white text-4xl" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-black text-slate-900">{expert.full_name}</h1>
                  {rating.count > 0 && (
                    <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                      <SafeIcon icon={FiStar} className="text-amber-500 fill-amber-500 text-sm" />
                      <span className="text-sm font-black text-amber-700">{rating.avg}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 text-slate-500 font-bold text-sm mt-2">
                  <span className="flex items-center gap-1.5"><SafeIcon icon={FiMapPin} className="text-green-500" /> {expert.city}, {expert.governorate}</span>
                  <span className="flex items-center gap-1.5"><SafeIcon icon={FiMail} className="text-blue-500" /> {expert.email}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="expert-card">
            <h2 className="text-xl font-black text-slate-900 mb-4">{t('modal.about')}</h2>
            <p className="text-slate-600 leading-relaxed font-medium">{expert.bio || 'No bio provided.'}</p>
          </div>

          {/* Reviews Section */}
          <div className="expert-card">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
                <SafeIcon icon={FiMessageSquare} className="text-blue-600" />
                {t('reviews.title')} ({rating.count})
              </h2>
              <button 
                onClick={() => setShowReviewForm(!showReviewForm)}
                className="text-sm font-black text-blue-600 hover:text-blue-700"
              >
                {showReviewForm ? t('modal.cancel') : t('reviews.leaveReview')}
              </button>
            </div>

            <AnimatePresence>
              {showSuccess && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-6 bg-blue-50 border border-blue-100 text-blue-700 p-4 rounded-xl flex items-center gap-3">
                  <SafeIcon icon={FiCheckCircle} className="text-xl" />
                  <p className="text-sm font-bold">Review submitted! It will appear once approved by our team.</p>
                </motion.div>
              )}

              {showReviewForm && (
                <motion.form 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  onSubmit={handleReviewSubmit}
                  className="bg-slate-50 border border-slate-100 rounded-2xl p-6 mb-8 overflow-hidden"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{t('reviews.nameLabel')}</label>
                      <input required value={newReview.reviewer_name} onChange={e => setNewReview({...newReview, reviewer_name: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none font-bold" placeholder="Your Name" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{t('reviews.ratingLabel')}</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button key={star} type="button" onClick={() => setNewReview({...newReview, rating: star})} className="text-2xl transition-transform active:scale-90">
                            <SafeIcon icon={FiStar} className={star <= newReview.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-300'} />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{t('reviews.commentLabel')}</label>
                    <textarea required value={newReview.comment} onChange={e => setNewReview({...newReview, comment: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none font-medium min-h-[100px]" placeholder="How was your session with this expert?" />
                  </div>
                  <button disabled={isSubmittingReview} className="w-full bg-blue-600 text-white py-4 rounded-xl font-black hover:bg-blue-700 transition-colors disabled:opacity-50">
                    {isSubmittingReview ? t('modal.sending') : t('reviews.submit')}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>

            {approvedReviews.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-slate-400 font-bold">{t('reviews.noReviews')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {approvedReviews.map((review) => (
                  <div key={review.id} className="border-b border-slate-50 last:border-0 pb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-black text-slate-900">{review.reviewer_name}</div>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <SafeIcon key={i} icon={FiStar} className={`text-xs ${i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm font-medium leading-relaxed">{review.comment}</p>
                    <div className="text-[10px] text-slate-400 font-bold uppercase mt-3">{new Date(review.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="expert-card bg-slate-900 text-white border-0 shadow-2xl">
            <h3 className="text-lg font-black mb-6">{t('modal.sendMessage')}</h3>
            <a href={`mailto:${expert.email}`} className="w-full bg-white text-slate-900 py-4 rounded-xl font-black flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors">
              <SafeIcon icon={FiMail} /> {t('modal.directEmail')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertProfile;